import React, { useState, useEffect, useContext } from 'react';
import { Select } from 'antd';
import { useLazyQuery } from '@apollo/react-hooks';
import styled from 'styled-components';
import FormLabel from '@material-ui/core/FormLabel';
import debounce from 'lodash.debounce';
import { UserContext } from 'Store/UserContext';
import { UPCOMING_VENUE_EVENTS } from 'Queries/Admin/UpcomingVenueEvents';
import IndeterminateLoading from 'Components/Loading/IndeterminateLoading';
import colors from '../../../styles/Colors';

const { Option } = Select;

const EventMultiSelectFilterBase = ({ field, form, ...props }) => {
  // eslint-disable-next-line no-unused-vars
  const { className, fieldlabel, value, clear, ...rest } = props;
  const { user } = useContext(UserContext);
  const [searchText, setSearchText] = useState('');
  const [events, setEvents] = useState([]);
  const [filteredEvents, setFilteredEvents] = useState([]);

  const [getVenueEvents, { data, loading, error }] = useLazyQuery(UPCOMING_VENUE_EVENTS, {
    fetchPolicy: 'network-only'
  });

  // Get events from current venue
  useEffect(() => {
    if (user && user.venues && user.venues.length > 0) {
      getVenueEvents({
        variables: {
          filterBy: {
            // Get events that haven't ended more than 30 days ago
            endDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
          }
        }
      });
    }
  }, [user, getVenueEvents]);

  // Process obtained events
  useEffect(() => {
    if (data && data.venue && data.venue.events) {
      // Sort events by ID (newest to oldest)
      const sortedEvents = [...data.venue.events].sort((a, b) => parseInt(b.id) - parseInt(a.id));
      setEvents(sortedEvents);
      setFilteredEvents(sortedEvents);
    }
  }, [data]);

  // Debounced search function
  const debouncedSearch = debounce(text => {
    if (!text || text.length === 0) {
      setFilteredEvents(events);
    } else {
      const filtered = events.filter(event => event.name.toLowerCase().includes(text.toLowerCase()));
      setFilteredEvents(filtered);
    }
  }, 300);

  const handleSearch = searchValue => {
    setSearchText(searchValue);
    debouncedSearch(searchValue);
  };

  const handleChange = selectedIds => {
    form.setFieldValue(field.name, selectedIds);
  };

  const handleClear = () => {
    form.setFieldValue(field.name, []);
    setSearchText('');
    setFilteredEvents(events);
  };

  const getSelectedEvents = () => {
    if (!value || value.length === 0) return [];
    return events.filter(event => value.includes(event.id));
  };

  if (error) {
    console.error('Error loading events:', error);
    return <div>Error loading events</div>;
  }
  getSelectedEvents();
  return (
    <div className={`${className}__event-multi-select-filter`}>
      <FormLabel>
        {fieldlabel}
        {value && value.length > 0 && (
          <button type="button" onClick={handleClear} className="clear">
            clear
          </button>
        )}
      </FormLabel>
      <Select
        {...rest}
        mode="multiple"
        showSearch
        value={value || []}
        placeholder="Search and select events"
        optionFilterProp="children"
        onChange={handleChange}
        onSearch={handleSearch}
        searchValue={searchText}
        allowClear
        style={{ width: '100%' }}
        loading={loading}
        filterOption={false} // Disable automatic filter because we use our own filter
        notFoundContent={loading ? <IndeterminateLoading /> : 'No events found'}
        maxTagCount="responsive"
        tagRender={props => {
          const { label, value: tagValue, closable, onClose } = props;
          const selectedEvent = events.find(e => e.id === tagValue);
          return (
            <span className="ant-select-selection-item">
              <span className="ant-select-selection-item-content">{selectedEvent ? selectedEvent.name : label}</span>
              {closable && (
                <span className="ant-select-selection-item-remove" onClick={onClose}>
                  ×
                </span>
              )}
            </span>
          );
        }}>
        {filteredEvents.map(event => (
          <Option key={event.id} value={event.id}>
            {event.name}
          </Option>
        ))}
      </Select>
    </div>
  );
};

const EventMultiSelectFilter = styled(EventMultiSelectFilterBase)`
  &__event-multi-select-filter {
    margin: 0 0 25px 0;

    .clear {
      color: #2875c3;
      float: right;
      text-transform: uppercase;
      font-family: 'IBMPlexSans-Bold';
      cursor: pointer;
      font-size: 12px;
      background: none;
      border: 0;
      padding: 0;
      position: relative;
      top: -2px;
    }

    label {
      display: block;
      text-transform: uppercase;
      font-family: 'IBMPlexSans-Bold';
      font-size: 12px;
      color: ${colors.text.secondary};
      margin-bottom: 10px;
    }

    .ant-select {
      font-size: 15px;

      .ant-select-selector {
        border-color: ${colors.text.lightGray2} !important;
        min-height: 40px;
        padding: 4px 8px;
      }

      .ant-select-selection-item {
        background: #f0f0f0;
        border: 1px solid #d9d9d9;
        border-radius: 4px;
        color: #333;
        font-size: 14px;
        padding: 2px 8px;
        margin: 2px 4px 2px 0;

        .ant-select-selection-item-remove {
          color: #999;
          cursor: pointer;
          font-size: 12px;
          margin-left: 6px;

          &:hover {
            color: #333;
          }
        }
      }

      .ant-select-selection-placeholder {
        color: #999;
        font-size: 15px;
      }

      &:hover .ant-select-selector {
        border-color: #2875c3 !important;
      }

      &.ant-select-focused .ant-select-selector {
        border-color: #2875c3 !important;
        box-shadow: 0 0 0 2px rgba(40, 117, 195, 0.2);
      }
    }

    .ant-select-dropdown {
      .ant-select-item {
        padding: 8px 12px;
        font-size: 15px;

        &:hover {
          background: #f5f5f5;
        }

        &.ant-select-item-option-selected {
          background: #e6f7ff;
          color: #2875c3;
        }
      }
    }
  }
`;

export default EventMultiSelectFilter;
